import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Shield, ShieldCheck, ShieldAlert, AlertTriangle, Check, X, Clock,
  Smartphone, Loader2, Activity, KeyRound, FileText,
} from "lucide-react";
import { toast } from "sonner";

export default function SecurityDashboard() {
  const [overview, setOverview] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [audit, setAudit] = useState([]);
  const [trusted, setTrusted] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [ov, co, al, au, td] = await Promise.all([
        api.get("/security/overview"),
        api.get("/security/compliance"),
        api.get("/security/alerts").catch(() => ({ data: [] })),
        api.get("/security/audit").catch(() => ({ data: [] })),
        api.get("/security/trusted-devices").catch(() => ({ data: [] })),
      ]);
      setOverview(ov.data);
      setCompliance(co.data);
      setAlerts(al.data || []);
      setAudit(au.data || []);
      setTrusted(td.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="security-page">
      <header>
        <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 flex items-center gap-2">
          <Shield className="w-3 h-3 text-amber-300" /> Tietoturva & Vaatimustenmukaisuus
        </p>
        <h1 className="text-3xl font-display gradient-text-luxe">Security Dashboard</h1>
        <p className="text-white/60 text-sm mt-1">Kirjautumiset, hälytykset, auditointi ja GDPR-kompiance.</p>
      </header>

      {loading && <Loader2 className="w-5 h-5 animate-spin text-white/40" />}

      {/* Compliance score hero */}
      {compliance && (
        <div className="glass-strong p-6 flex items-center gap-6 flex-wrap">
          <div className="relative w-32 h-32 flex-shrink-0">
            <svg className="w-32 h-32 transform -rotate-90">
              <circle cx="64" cy="64" r="56" stroke="rgba(255,255,255,0.08)" strokeWidth="10" fill="none" />
              <circle
                cx="64" cy="64" r="56"
                stroke={compliance.score > 80 ? "#10b981" : compliance.score > 50 ? "#f59e0b" : "#ef4444"}
                strokeWidth="10" fill="none"
                strokeDasharray={2 * Math.PI * 56}
                strokeDashoffset={2 * Math.PI * 56 * (1 - compliance.score / 100)}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <p className="text-3xl font-display font-bold text-white">{compliance.score}%</p>
              <p className="text-[10px] uppercase tracking-widest text-white/40">Compliance</p>
            </div>
          </div>
          <div className="flex-1 min-w-[260px]">
            <h2 className="text-xl font-display text-white">Vaatimustenmukaisuus</h2>
            <p className="text-sm text-white/60 mb-3">{compliance.passed} / {compliance.total} kohtaa kunnossa</p>
            <div className="space-y-1.5">
              {compliance.checks.map((c) => (
                <div key={c.id} className="flex items-center gap-2 text-xs">
                  {c.passed ? <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" /> : <X className="w-4 h-4 text-red-400 flex-shrink-0" />}
                  <span className={c.passed ? "text-white/80" : "text-white/50 line-through"}>{c.title}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* KPI */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Stat icon={<ShieldCheck className="w-4 h-4" />} label="Onnistuneita kirjautumisia / 7vrk" value={overview.successful_logins_week} c="emerald" />
          <Stat icon={<ShieldAlert className="w-4 h-4" />} label="Epäonnistuneita / 7vrk" value={overview.failed_logins_week} c="red" />
          <Stat icon={<AlertTriangle className="w-4 h-4" />} label="Aktiiviset hälytykset" value={overview.active_alerts} c="amber" />
          <Stat icon={<AlertTriangle className="w-4 h-4" />} label="Kriittiset hälytykset" value={overview.critical_alerts} c="red" />
          <Stat icon={<Smartphone className="w-4 h-4" />} label="Luotetut laitteet" value={overview.trusted_devices} c="blue" />
        </div>
      )}

      {/* Two columns: login history + alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass-card p-6">
          <h2 className="text-base font-display text-white mb-4 relative z-10 flex items-center gap-2">
            <Activity className="w-4 h-4 text-amber-400" /> Viimeisimmät kirjautumiset
          </h2>
          <div className="space-y-2 relative z-10">
            {(overview?.recent_logins || []).length === 0
              ? <p className="text-white/40 text-sm">Ei kirjautumistietoja vielä</p>
              : overview.recent_logins.map((l) => (
                <div key={l.id} className="flex items-center justify-between p-2 rounded-lg bg-white/[0.02] border border-white/5">
                  <div>
                    <p className="text-sm text-white/90">{l.email}</p>
                    <p className="text-[10px] text-white/40 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {l.created_at?.slice(0, 16).replace("T", " ")}
                    </p>
                  </div>
                  <Badge className={`text-[10px] border-0 ${l.success ? "bg-emerald-500/20 text-emerald-200" : "bg-red-500/20 text-red-200"}`}>
                    {l.success ? "OK" : "FAIL"}
                  </Badge>
                </div>
              ))}
          </div>
        </div>

        <div className="glass-card p-6">
          <h2 className="text-base font-display text-white mb-4 relative z-10 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" /> Hälytykset
          </h2>
          <div className="space-y-2 relative z-10">
            {alerts.length === 0
              ? <p className="text-white/40 text-sm">Ei aktiivisia hälytyksiä</p>
              : alerts.slice(0, 8).map((a) => (
                <div key={a.id} className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-white/90">{a.title}</p>
                    <Badge className={`text-[10px] border-0 ${
                      a.severity === "critical" ? "bg-red-500/20 text-red-200" :
                      a.severity === "warning" ? "bg-amber-500/20 text-amber-200" :
                      "bg-blue-500/20 text-blue-200"
                    }`}>{a.severity}</Badge>
                  </div>
                  {a.description && <p className="text-xs text-white/50 mt-1">{a.description}</p>}
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Audit log */}
      <div className="glass-card p-6">
        <h2 className="text-base font-display text-white mb-4 relative z-10 flex items-center gap-2">
          <FileText className="w-4 h-4 text-amber-400" /> Auditointiloki
        </h2>
        <div className="overflow-x-auto relative z-10">
          {audit.length === 0
            ? <p className="text-white/40 text-sm">Ei audit-merkintöjä vielä</p>
            : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5">
                    <th className="py-2 px-2">Aika</th>
                    <th className="py-2 px-2">Käyttäjä</th>
                    <th className="py-2 px-2">Toiminto</th>
                    <th className="py-2 px-2">Kohde</th>
                    <th className="py-2 px-2">Riski</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.slice(0, 20).map((a) => (
                    <tr key={a.id} className="border-b border-white/5 text-xs">
                      <td className="py-2 px-2 text-white/60">{a.created_at?.slice(0, 16).replace("T", " ")}</td>
                      <td className="py-2 px-2 text-white/80">{a.actor_email || "—"}</td>
                      <td className="py-2 px-2 text-amber-300 font-mono">{a.action}</td>
                      <td className="py-2 px-2 text-white/70">{a.entity}</td>
                      <td className="py-2 px-2">
                        <Badge className={`text-[10px] border-0 ${
                          a.risk_level === "critical" ? "bg-red-500/20 text-red-200" :
                          a.risk_level === "high" ? "bg-amber-500/20 text-amber-200" :
                          "bg-zinc-500/20 text-zinc-200"
                        }`}>{a.risk_level}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, c }) {
  const colors = { amber: "text-amber-300", emerald: "text-emerald-300", red: "text-red-300", blue: "text-blue-300" };
  return (
    <div className="stat-block">
      <div className={`w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center ${colors[c]} mb-2`}>{icon}</div>
      <p className="text-[10px] uppercase tracking-widest text-white/40">{label}</p>
      <p className={`text-2xl font-display font-bold ${colors[c]}`}>{value}</p>
    </div>
  );
}
